import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { integrationService, WFirmaContractorCandidate } from '../services/integrationService';
import { Counterparty } from '../types/counterparty';
import { useTranslation } from '../hooks/useTranslation';

interface WfirmaLinkCounterpartyModalProps {
  companyId: string;
  counterparty: Counterparty | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WfirmaLinkCounterpartyModal: React.FC<WfirmaLinkCounterpartyModalProps> = ({
  companyId,
  counterparty,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [nameQ, setNameQ] = useState('');
  const [nipQ, setNipQ] = useState('');
  const [vatQ, setVatQ] = useState('');
  const [candidates, setCandidates] = useState<WFirmaContractorCandidate[]>([]);
  const [selectedWfirmaId, setSelectedWfirmaId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && counterparty) {
      setNameQ(counterparty.name || '');
      setNipQ(counterparty.taxId || '');
      setVatQ(counterparty.vatId || '');
      setCandidates([]);
      setSelectedWfirmaId(null);
    }
  }, [isOpen, counterparty]);

  const searchMutation = useMutation({
    mutationFn: () =>
      integrationService.searchWFirmaContractorCandidates(companyId, {
        name: nameQ.trim() || undefined,
        nip: nipQ.trim() || undefined,
        vat: vatQ.trim() || undefined
      }),
    onSuccess: (list) => {
      setCandidates(list);
      setSelectedWfirmaId(null);
    }
  });

  const linkMutation = useMutation({
    mutationFn: (wfirmaId: number) =>
      integrationService.linkCounterpartyToWfirma(companyId, counterparty!.id, wfirmaId),
    onSuccess: () => {
      onSuccess();
      onClose();
    }
  });

  if (!isOpen || !counterparty) {
    return null;
  }

  const canSearch = Boolean(nameQ.trim() || nipQ.trim() || vatQ.trim());

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-12 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('companies.counterparties.wfirmaLinkTitle')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{counterparty.name}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('companies.counterparties.wfirmaLinkName')}
            </label>
            <input
              type="text"
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('companies.counterparties.taxId')} (NIP)
            </label>
            <input
              type="text"
              value={nipQ}
              onChange={(e) => setNipQ(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('companies.counterparties.vatId')}
            </label>
            <input
              type="text"
              value={vatQ}
              onChange={(e) => setVatQ(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => searchMutation.mutate()}
            disabled={!canSearch || searchMutation.isPending}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {searchMutation.isPending
              ? t('companies.counterparties.wfirmaLinkSearching')
              : t('companies.counterparties.wfirmaLinkSearch')}
          </button>
        </div>

        {searchMutation.isError && (
          <p className="text-sm text-red-600 mb-2">
            {(searchMutation.error as Error)?.message || t('companies.counterparties.wfirmaLinkSearchError')}
          </p>
        )}

        {candidates.length === 0 && !searchMutation.isPending && searchMutation.isSuccess && (
          <p className="text-sm text-gray-500 mb-4">{t('companies.counterparties.wfirmaLinkNoResults')}</p>
        )}

        {candidates.length > 0 && (
          <div className="border rounded-md overflow-x-auto max-h-72 overflow-y-auto mb-4">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-700 w-10" />
                  <th className="px-2 py-2 text-left font-medium text-gray-700">ID wFirma</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">
                    {t('companies.counterparties.wfirmaLinkColName')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">NIP / VAT</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">
                    {t('companies.counterparties.city')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">
                    {t('companies.counterparties.wfirmaLinkColCountry')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">
                    {t('companies.counterparties.wfirmaLinkColTaxType')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr
                    key={c.wfirmaId}
                    className={selectedWfirmaId === c.wfirmaId ? 'bg-blue-50' : 'hover:bg-gray-50'}
                  >
                    <td className="px-2 py-1">
                      <input
                        type="radio"
                        name="wfirmaCandidate"
                        checked={selectedWfirmaId === c.wfirmaId}
                        onChange={() => setSelectedWfirmaId(c.wfirmaId)}
                      />
                    </td>
                    <td className="px-2 py-1 font-mono">{c.wfirmaId}</td>
                    <td className="px-2 py-1">{c.name || '—'}</td>
                    <td className="px-2 py-1 font-mono">{c.nip || '—'}</td>
                    <td className="px-2 py-1">{c.city || '—'}</td>
                    <td className="px-2 py-1">{c.country || '—'}</td>
                    <td className="px-2 py-1">{c.taxIdType || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {linkMutation.isError && (
          <p className="text-sm text-red-600 mb-2">
            {(linkMutation.error as any)?.response?.data?.message ||
              (linkMutation.error as Error)?.message ||
              t('companies.counterparties.wfirmaLinkError')}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            {t('common.close') || 'Закрыть'}
          </button>
          <button
            type="button"
            disabled={selectedWfirmaId == null || linkMutation.isPending}
            onClick={() => selectedWfirmaId != null && linkMutation.mutate(selectedWfirmaId)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {linkMutation.isPending
              ? t('companies.counterparties.wfirmaLinkLinking')
              : t('companies.counterparties.wfirmaLinkConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WfirmaLinkCounterpartyModal;
